import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import classNames from "classnames";
import React, { useCallback, useMemo, useState } from "react";
import { useIntl } from "react-intl";
import { Button, Col, Container, Form, Row, Spinner } from "reactstrap";
import { toast } from "react-toastify";
import { OPENAI_SOCIAL_FORM } from "../../../../constants/modal.constant";
import { useGlobalCore } from "../../../../cores/globals";
import { useMeCore } from "../../../../cores/me";
import { useOpenAiCore } from "../../../../cores/openAi";
import { SocialTemplate } from "../../../../cores/openAi/interface/api/get-social-templates";
import { useSocialPostCore } from "../../../../cores/socialPost";
import { PostImage } from "../../../../cores/socialPost/interfaces/postImage";
import { generateOpenAiSocialImage } from "../../../../cores/helpers/openai-generate-social-image";
import socialApiClient from "../../../../cores/helpers/services/social-api-client";
import { buildSocialPostImagePrompt } from "./buildSocialPostImagePrompt";
import { CheckboxWembii } from "../../CheckboxWembii";
import { InputWembii } from "../../InputWembii";
import { useEmailCore } from "../../../../cores/email";
import "./OpenAiForm.scss";

export default function OpenAiForm({
  emailTemplates,
}: {
  emailTemplates?: boolean;
}) {
  const intl = useIntl();
  const { clear, addText, addImages, socialProviders } = useSocialPostCore();
  const {
    templates,
    showTemplates,
    setShowTemplates,
    getSocialTemplate,
    getEmailTemplate,
  } = useOpenAiCore();
  const { company } = useMeCore();
  const { turnOffModal } = useGlobalCore();
  const { setMessage } = useEmailCore();
  const [selected, setSelected] = useState(0);
  const [industry, setIndustry] = useState("");
  const [subject, setSubject] = useState("");
  const [additionalInfo, setAdditionalInfo] = useState("");
  const [emoticons, setEmoticons] = useState(true);
  const [hashtags, setHashtags] = useState(true);
  const [generateAiPost, setGenerateAiPost] = useState(false);
  const [generatedImage, setGeneratedImage] = useState<PostImage | null>(null);
  const [generatedTemplates, setGeneratedTemplates] = useState<
    SocialTemplate[] | null
  >(null);
  const [isGeneratingAiPost, setIsGeneratingAiPost] = useState(false);

  const mapGenerationError = useCallback((error: any) => {
    const status = error?.response?.status || error?.request?.status;
    const responseMessage =
      error?.response?.data?.message ||
      error?.response?.data?.error?.message ||
      error?.message;

    if (status === 504) {
      return "La generaci?n tard? m?s de lo esperado y el servidor cort? la espera. Intenta nuevamente en unos segundos.";
    }

    if (status === 422) {
      return "No pudimos completar esta generaci?n con esos datos. Intenta simplificar la tem?tica o la informaci?n adicional.";
    }

    if (status === 403) {
      return "OpenAI rechaz? la generaci?n. Revisa la API key en Integraciones o el saldo de tu cuenta.";
    }

    if (status === 404) {
      return "El backend a?n no tiene disponible openai/images. Hay que desplegar ese endpoint en la API social.";
    }

    return responseMessage || "No se pudo generar la publicación con IA.";
  }, []);

  const buildImagePrompt = useCallback(
    () =>
      buildSocialPostImagePrompt({
        industry,
        subject,
        additionalInfo,
        platform: "Instagram",
        brandName: company?.name,
      }),
    [additionalInfo, company?.name, industry, subject]
  );

  const generateImageForPost = useCallback(async () => {
    if (!company?.id) {
      throw new Error("No hay empresa seleccionada para generar la imagen.");
    }

    try {
      return await generateOpenAiSocialImage(
        company.id,
        buildImagePrompt(),
        "1024x1024"
      );
    } catch (error: any) {
      throw new Error(mapGenerationError(error));
    }
  }, [buildImagePrompt, company?.id, mapGenerationError]);

  const getSocialTemplatesDirect = useCallback(
    async (body: any) => {
      if (!company?.id) {
        throw new Error("No hay empresa seleccionada para generar el texto.");
      }

      try {
        const { data } = await socialApiClient().post("openai/posts", body, {
          params: { companyId: company.id },
        });
        const payload = data?.payload ?? data;
        const responseTemplates = Array.isArray(payload?.templates)
          ? payload.templates
          : payload?.template
          ? [payload.template]
          : Array.isArray(data?.templates)
          ? data.templates
          : [];

        if (!responseTemplates.length) {
          throw new Error("La IA no devolvió texto para la publicación.");
        }

        return responseTemplates as SocialTemplate[];
      } catch (error: any) {
        throw new Error(mapGenerationError(error));
      }
    },
    [company?.id, mapGenerationError]
  );

  const onSelecting = useCallback(
    (template) => {
      clear();
      if (emailTemplates) {
        setMessage(template.body);
      } else {
        addText(template.body);
        if (generatedImage) {
          addImages([generatedImage]);
        }
      }
      turnOffModal(OPENAI_SOCIAL_FORM);
    },
    [clear, emailTemplates, setMessage, addText, generatedImage, addImages, turnOffModal]
  );

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();

      const socialMedias = socialProviders.map((social) => social.name!);
      if (emailTemplates) {
        getEmailTemplate(company?.id!, {
          industry,
          subject,
          emoticons,
          hashtags,
          additionalInfo,
          socialMedias,
        });
      } else {
        setGeneratedImage(null);
        setGeneratedTemplates(null);
        setSelected(0);
        const requestBody = {
          industry,
          subject,
          emoticons,
          hashtags,
          additionalInfo,
          socialMedias,
        };

        if (generateAiPost) {
          try {
            setIsGeneratingAiPost(true);
            const [image, aiTemplates] = await Promise.all([
              generateImageForPost(),
              getSocialTemplatesDirect(requestBody),
            ]);
            setGeneratedImage(image);
            setGeneratedTemplates(aiTemplates);
          } catch (error: any) {
            console.error("OpenAiForm AI post generation error:", error);
            toast.error(mapGenerationError(error));
            return;
          } finally {
            setIsGeneratingAiPost(false);
          }
          return;
        }

        try {
          setIsGeneratingAiPost(true);
          await getSocialTemplate(company?.id!, requestBody);
        } finally {
          setIsGeneratingAiPost(false);
        }
      }
    },
    [
      industry,
      subject,
      emoticons,
      hashtags,
      additionalInfo,
      company,
      socialProviders,
      getSocialTemplate,
      getEmailTemplate,
      generateAiPost,
      generateImageForPost,
      getSocialTemplatesDirect,
      mapGenerationError,
    ]
  );

  const displayTemplates = useMemo(
    () => generatedTemplates ?? templates,
    [generatedTemplates, templates]
  );
  const shouldShowTemplates = showTemplates || generatedTemplates !== null;

  const template = useMemo(
    () => displayTemplates[selected] || null,
    [displayTemplates, selected]
  );

  const increase = useCallback(() => {
    if (selected < displayTemplates.length - 1) {
      setSelected(selected + 1);
    }
  }, [selected, displayTemplates]);

  const decrease = useCallback(() => {
    if (selected > 0) {
      setSelected(selected - 1);
    }
  }, [selected]);

  const back = useCallback(() => {
    setGeneratedTemplates(null);
    setShowTemplates(false);
  }, [setShowTemplates]);

  const next = useCallback(() => {
    setShowTemplates(true);
  }, [setShowTemplates]);

  return (
    <Container className="h-100 openai-form" fluid>
      <Row className="h-100">
        <Col xs="12">
          <div
            className={classNames("d-flex flex-column h-100 p-3", {
              "justify-content-between": shouldShowTemplates,
            })}
          >
            <div className="form-header">
              <h2 className="h2 d-flex justify-content-center align-items-center mb-4">
                {shouldShowTemplates
                  ? intl.formatMessage({ id: "app.socialStory.template" })
                  : intl.formatMessage({ id: "app.openAiForm.title" })}
              </h2>
              {!shouldShowTemplates && (
                <p className="openai-subtitle">
                  {intl.formatMessage({ id: "app.openAiForm.subtitle" })}
                </p>
              )}
            </div>

            {shouldShowTemplates ? (
              <>
                <div className="template-section">
                  <div className="template-navigation">
                    <button
                      className="nav-button"
                      onClick={decrease}
                      disabled={selected === 0}
                    >
                      <FontAwesomeIcon icon={["fas", "angle-left"]} size="lg" />
                    </button>

                    <div className="template-content">
                      {template ? (
                        <div className="d-flex flex-column align-items-center">
                          <div className="template-text pre-wrap">{template.body}</div>

                          {generatedImage && (
                            <div className="template-image-preview mt-3">
                              <img
                                src={generatedImage.data}
                                alt="Publicación generada con IA"
                              />
                            </div>
                          )}

                          <div className="template-counter text-muted mt-3">
                            {selected + 1} / {displayTemplates.length}
                          </div>

                          <Button
                            color="primary"
                            className="mt-3"
                            onClick={() => onSelecting(template)}
                          >
                            {intl.formatMessage({
                              id: "app.socialStory.template.select",
                            })}
                          </Button>
                        </div>
                      ) : (
                        <label className="text-muted">
                          {intl.formatMessage({ id: "app.socialStory.template.empty" })}
                        </label>
                      )}
                    </div>

                    <button
                      className="nav-button"
                      onClick={increase}
                      disabled={selected === displayTemplates.length - 1}
                    >
                      <FontAwesomeIcon icon={["fas", "angle-right"]} size="lg" />
                    </button>
                  </div>
                </div>

                <Button
                  className="my-1 mt-3"
                  color="white"
                  block
                  onClick={back}
                >
                  {intl.formatMessage({ id: "app.openAiForm.regenerate" })}
                </Button>
              </>
            ) : (
              <div className="d-flex flex-column form-section">
                <Form onSubmit={handleSubmit}>
                  <div className="form-group">
                    <InputWembii
                      label={intl.formatMessage({ id: "app.openAiForm.industry" })}
                      type="text"
                      name="industry"
                      required
                      placeholder={intl.formatMessage({ id: "app.openAiForm.industry" })}
                      value={industry}
                      onChange={setIndustry}
                    />
                  </div>

                  <div className="form-group">
                    <InputWembii
                      label={intl.formatMessage({
                        id: emailTemplates
                          ? "app.openAiForm.purpose"
                          : "app.openAiForm.topic",
                      })}
                      type="text"
                      name="subject"
                      required
                      placeholder={intl.formatMessage({
                        id: emailTemplates
                          ? "app.openAiForm.purpose"
                          : "app.openAiForm.topic",
                      })}
                      value={subject}
                      onChange={setSubject}
                    />
                  </div>

                  <div className="form-group">
                    <InputWembii
                      label={intl.formatMessage({ id: "app.openAiForm.additionalInfo" })}
                      type="textarea"
                      name="additional"
                      required
                      placeholder={intl.formatMessage({ id: "app.openAiForm.additionalInfo" })}
                      value={additionalInfo}
                      onChange={setAdditionalInfo}
                    />
                  </div>

                  <div className="checkbox-section">
                    <div className="checkbox-item">
                      <CheckboxWembii
                        label={intl.formatMessage({ id: "app.openAiForm.noEmoticons" })}
                        checked={!emoticons}
                        onChange={() => setEmoticons(!emoticons)}
                      />
                    </div>

                    <div className="checkbox-item">
                      <CheckboxWembii
                        label={intl.formatMessage({ id: "app.openAiForm.noHashtags" })}
                        checked={!hashtags}
                        onChange={() => setHashtags(!hashtags)}
                      />
                    </div>

                    {!emailTemplates && (
                      <div className="checkbox-item">
                        <CheckboxWembii
                          label="Generar publicación con IA"
                          checked={generateAiPost}
                          onChange={() => setGenerateAiPost(!generateAiPost)}
                        />
                      </div>
                    )}
                  </div>

                  {isGeneratingAiPost && (
                    <div className="openai-generation-status" role="status">
                      <div className="openai-generation-status__animation">
                        <span />
                        <span />
                        <span />
                      </div>
                      <div>
                        <strong>
                          En este momento estamos generando las publicaciones con IA
                        </strong>
                        <p>
                          Permanece en esta pantalla aunque demore. Estamos
                          creando la imagen y preparando el contenido para tu
                          publicación.
                        </p>
                      </div>
                    </div>
                  )}

                  <div className="button-section">
                    {displayTemplates.length > 0 && (
                      <Button
                        className="my-1 mt-4"
                        color="white"
                        block
                        onClick={next}
                      >
                        {intl.formatMessage({ id: "app.openAiForm.viewLastGenerated" })}
                      </Button>
                    )}
                    <Button
                      type="submit"
                      className="my-1 mt-3"
                      color="primary"
                      block
                      disabled={isGeneratingAiPost}
                    >
                      {isGeneratingAiPost && <Spinner size="sm" className="mr-2" />}
                      {isGeneratingAiPost
                        ? "Generando publicaciones con IA..."
                        : intl.formatMessage({ id: "app.openAiForm.generate" })}
                    </Button>
                  </div>
                </Form>
              </div>
            )}
          </div>
        </Col>
      </Row>
    </Container>
  );
}
